import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AppIcons } from 'app/modules/shared/app.icons';
import { AppSettings } from 'app/modules/shared/app.settings';
import { CommonBindingDataService } from 'app/modules/shared/services/common-binding-data.service';
import { ConfigService } from 'app/modules/shared/services/config.service';
import { EntityService } from 'app/modules/shared/services/entity.service';
import { MenuItem, MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MenuModule } from 'primeng/menu';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-change-review-status',
  standalone: true,
  imports: [DropdownModule,
    FormsModule,
    DialogModule,
    InputTextModule,
    ButtonModule,
    ReactiveFormsModule,
    CommonModule,
    TranslateModule,
    InputTextareaModule,
    MenuModule
  ],
  templateUrl: './change-review-status.component.html',
  styleUrl: './change-review-status.component.scss'
})
export class ChangeReviewStatusComponent implements OnInit {

  @Input() attributeValues: any;
  protocolDetails: any;
  showDialog: boolean = false;
  approvalReason: string = '';
  visible: boolean = false;
  protocolId;
  comment;
  miIcons = AppIcons;
  entityCode = AppSettings.ENTITY_CODE.CHANGE_REVIEW;
  options = [
    { label: 'Approve', value: 'Approved', icon: 'pi-check-circle' },
    { label: 'Reject', value: 'Rejected', icon: 'pi-times-circle' },
  ];
  dialogHeader: string = '';
  dialogMessage: string = '';
  dialogActionButtonLabel: string = '';
  selectedAction: string = '';
  commentForm: FormGroup;
  originalStatus;
  subscription: Subscription;
  userIdLogin;
  createdByProtocol;
  selectedStatus: string;
  displayedStatus: string;
  status: string = 'Pending';
  rowID: Subscription;
  currentRow;

  constructor(private trialReviewService: EntityService,
    private configService: ConfigService,
    private messageService: MessageService,
    private cs: CommonBindingDataService,
    private fb: FormBuilder,
  ) { }

  ngOnInit() {
    this.createdByProtocol = this.attributeValues.createdBy;
    this.subscription = this.cs.userId$.subscribe(userId => {
      if (userId) {
        this.userIdLogin = userId;
      }
    });
    this.commentInput();
    this.status = this.attributeValues.status;
    this.originalStatus = this.attributeValues.status;
    this.protocolId = this.attributeValues.protocolReviewRequestId;
    this.comment = this.attributeValues.reviewerComment;
    this.status = this.attributeValues.status;
  }

  // onMenuOpen() {
  //  this.rowID =  this.trialReviewService.entityCurrentRow.subscribe(res => {
  //     this.currentRow = res;
  //   });
  // }

  menuItems: MenuItem[] = [
    {
      label: 'Approve',
      icon: `${AppIcons.CIRCLE_CHECKED} wh-16`,
      command: () => {
        this.selectedStatus = 'Approved';
        this.onApply();
      }
    },
    {
      separator: true
    },
    {
      label: 'Reject',
      icon: `${AppIcons.OFF_OUTLINE_CLOSE} wh-16`,
      command: () => {
        this.selectedStatus = 'Rejected';
        this.onApply();
      }
    }
  ];

  // getLabelFromValue(value: string): string {
  //   const selectedOption = this.options.find(option => option.value === value);
  //   return selectedOption ? selectedOption.label : value;
  // }

  getDropdownBgColor(): string {
    if (this.status === 'Approved') {
      return '#32cd3226';
    } else if (this.status === 'Rejected') {
      return '#ff370026';
    } else if (this.status === 'Pending') {
      return '#ffa50026';
    }
  }

  commentInput() {
    this.commentForm = this.fb.group({
      approvalReason: ['', [Validators.required, Validators.maxLength(500), this.noWhitespaceValidator()]],
    });
  }

  noWhitespaceValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value && control.value.trim() === '') {
        return { whitespaceOnly: true };
      }
      return null;
    };
  }

  canUpdateStatus(): boolean {
    return this.createdByProtocol !== this.userIdLogin;
  }

  getActionLabel(status: string): string {
    switch (status) {
      case 'Approved':
        return 'Approve';
      case 'Rejected':
        return 'Reject';
      default:
        return 'Pending';
    }
  }

  onApply() {
    if (!this.canUpdateStatus()) {
      this.messageService.add({
        key: 'tst',
        severity: 'error',
        summary: 'Error',
        detail: this.cs.getLabel('lbl_reviewer_error_msg'),
      });
      return;
    }

    const actionLabel = this.getActionLabel(this.selectedStatus);

    if (this.selectedStatus === 'Approved' || this.selectedStatus === 'Rejected') {
      this.selectedAction = this.selectedStatus;
      this.dialogHeader = `${actionLabel} Protocol`;
      this.dialogMessage = `Are you sure you want to ${actionLabel} the protocol?`;
      this.dialogActionButtonLabel = actionLabel;
      this.commentForm.reset();
      this.showDialog = true;
    } else {
      this.status = this.selectedStatus;
      this.callUpdateReviewStatus();
    }

  }

  onDialogCancel() {
    this.status = this.originalStatus;
    this.showDialog = false;
  }

  onDialogConfirm() {
    if (this.commentForm.invalid) {
      this.commentForm.markAllAsTouched();
      return;
    }

    this.comment = this.commentForm.get('approvalReason')?.value;
    this.showDialog = false;
    this.originalStatus = this.status;
    this.callUpdateReviewStatus();
  }

  private callUpdateReviewStatus() {
    const payload = {
      forTenantCode: this.configService.getLoggedInTenantCode(),
      protocolReviewRequestId: this.protocolId,
      status: this.selectedStatus,
      reviewerComment: this.comment
    };

    this.trialReviewService.updateReviewStatus(this.entityCode, payload).subscribe({
      next: () => {
        this.status = this.selectedStatus;
        this.showDialog = false;
        this.messageService.add({
          key: 'tst',
          severity: 'success',
          summary: 'Successful',
          detail: this.cs.getLabel('change_review.lbl_status_update_msg'),
        });
      }
    });
  }

  onHide() {
    this.visible = false;
  }

}
